import type { ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { EditableRow } from "@/components/design/EditableRow";
import { EntryFields } from "@/components/design/EntryFields";
import { SkillLevelInfoIcon } from "@/components/design/SkillLevelInfoIcon";
import type { FieldDef, FieldOption } from "@/lib/field-util";
import { isFieldMissingInEntry } from "@/lib/field-util";
import { CEFR_OPTIONS, languageLabel } from "@/lib/language-options";
import { useUiStore } from "@/stores/ui-store";
import { levelFromPercent } from "@/lib/skill-levels";
import { ForcedShare } from "./fields";
import type {
  CertificateEntry,
  EducationEntry,
  ExpertiseEntry,
  LanguageEntry,
} from "./types";

const EDU_MONTHS = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"] as const;

function fmtMonthYear(v: string | null | undefined): string {
  if (!v) return "";
  const m = String(v).match(/(\d{4})-(\d{2})/);
  if (m) return `${EDU_MONTHS[Number(m[2]) - 1]} ${m[1]}`;
  const y = String(v).match(/\d{4}/);
  return y ? y[0] : "";
}

function computeMissing(
  fields: readonly FieldDef[],
  entry: Record<string, unknown>,
): string[] {
  return fields.filter((f) => isFieldMissingInEntry(f, entry)).map((f) => f.label);
}

function EntryRowError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <div className="smycles-field-meta mt-1 text-ink-accent">⚠ {message}</div>
  );
}

export function EducationRow({
  entry,
  editable,
  fields,
  countryOptions,
  degreeOptions,
  isDraft = false,
  error,
  onPatch,
  onCancel,
  onDelete,
  shareControl,
}: {
  entry: EducationEntry;
  editable: boolean;
  fields: readonly FieldDef[];
  countryOptions: readonly FieldOption[];
  degreeOptions: readonly FieldOption[];
  isDraft?: boolean;
  error?: string;
  onPatch: (patch: Record<string, unknown>) => void;
  onCancel?: () => void;
  onDelete?: () => void;
  shareControl?: ReactNode;
}) {
  const intl = useIntl();
  const degreeLabel = degreeOptions.find((o) => o.value === entry.degree)?.label || entry.degree;
  const title = [degreeLabel, entry.field].filter(Boolean).join(" · ") || entry.institution || "–";
  const countryLabel = countryOptions.find((o) => o.value === entry.country)?.label || entry.country;
  const place = [countryLabel, entry.city].filter(Boolean).join(", ");
  // The start is optional: a missing end reads as "today" only after a known start, an end alone is "until",
  // and an education without dates shows no period at all.
  const start = fmtMonthYear(entry.startDate);
  const end = fmtMonthYear(entry.endDate);
  const span = start
    ? `${start} – ${end || intl.formatMessage({ id: "profile.rows.today", defaultMessage: "today" })}`
    : end
      ? intl.formatMessage({ id: "profile.rows.until", defaultMessage: "until {date}" }, { date: end })
      : "";
  const missing = computeMissing(fields, entry as unknown as Record<string, unknown>);
  return (
    <EditableRow
      rowId={`profile-education-${entry.id}`}
      id={`profile-education-${entry.id}`}
      editable={editable}
      className="py-2.5 first:pt-0 last:pb-0"
      onCancel={onCancel}
      onDelete={onDelete}
      missingFields={missing}
      topRight={shareControl ? <ForcedShare>{shareControl}</ForcedShare> : undefined}
      confirmLabel={
        isDraft
          ? intl.formatMessage({ id: "common.add", defaultMessage: "Add" })
          : intl.formatMessage({ id: "profile.rows.done", defaultMessage: "Done" })
      }
      renderDisplay={() => (
        <>
          <div className="flex items-baseline justify-between gap-2">
            <div
              id={`profile-education-${entry.id}-title`}
              className="text-smycles-body font-medium leading-tight transition group-hover:text-ink-accent"
            >
              {title}
            </div>
            {shareControl}
          </div>
          {entry.institution && (entry.field || degreeLabel) ? (
            <div className="smycles-field-meta text-ink-muted">{entry.institution}</div>
          ) : null}
          {span ? <div className="smycles-field-meta text-ink-muted">{span}</div> : null}
          {place ? <div className="smycles-field-meta text-ink-muted">{place}</div> : null}
          <EntryRowError message={error} />
        </>
      )}
      renderEdit={() => (
        <EntryFields
          fields={fields}
          values={entry as unknown as Record<string, string>}
          onChange={(k, v) => onPatch({ [k]: v })}
        />
      )}
    />
  );
}

export function LanguageRow({
  idPrefix = "profile",
  entry,
  editable,
  fields,
  isDraft = false,
  error,
  onPatch,
  onCancel,
  onDelete,
  shareControl,
}: {
  idPrefix?: string;
  entry: LanguageEntry;
  editable: boolean;
  fields: readonly FieldDef[];
  isDraft?: boolean;
  error?: string;
  onPatch: (patch: Record<string, unknown>) => void;
  onCancel?: () => void;
  onDelete?: () => void;
  shareControl?: ReactNode;
}) {
  const intl = useIntl();
  const locale = useUiStore((s) => s.locale);
  const langLabel = languageLabel(entry.languageTag, locale) || "–";
  const levelLabel = entry.nativeSpeaker
    ? intl.formatMessage({ id: "profile.rows.nativeSpeaker", defaultMessage: "Native speaker" })
    : CEFR_OPTIONS.find((o) => o.value === entry.cefr)?.label
      || intl.formatMessage({ id: "profile.rows.noLevel", defaultMessage: "No level" });
  const missing = computeMissing(fields, entry as unknown as Record<string, unknown>);
  // Der Sprachcode ist der stabile fachliche Schlüssel der Zeile, nicht ihr
  // Listenplatz und nicht die übersetzte Bezeichnung.
  const rowId = `${idPrefix}-language-${entry.languageTag}`;
  return (
    <EditableRow
      rowId={rowId}
      id={rowId}
      editable={editable}
      className="py-2.5 first:pt-0 last:pb-0"
      onCancel={onCancel}
      onDelete={onDelete}
      missingFields={missing}
      topRight={shareControl}
      confirmLabel={
        isDraft
          ? intl.formatMessage({ id: "common.add", defaultMessage: "Add" })
          : intl.formatMessage({ id: "profile.rows.done", defaultMessage: "Done" })
      }
      renderDisplay={() => (
        <>
          <div className="flex items-baseline gap-3">
            <div className="min-w-0 flex-1">
              <div
                id={`${rowId}-name`}
                className="text-smycles-body font-medium leading-tight transition group-hover:text-ink-accent"
              >
                {langLabel}
              </div>
              <div id={`${rowId}-level`} className="smycles-field-meta text-ink-muted">
                {levelLabel}
              </div>
            </div>
            {shareControl}
          </div>
          <EntryRowError message={error} />
        </>
      )}
      renderEdit={() => (
        <EntryFields
          fields={fields}
          values={entry as unknown as Record<string, string | boolean>}
          onChange={(k, v) => onPatch({ [k]: v })}
        />
      )}
    />
  );
}

export function ExpertiseRow({
  entry,
  editable,
  fields,
  isDraft = false,
  error,
  onPatch,
  onCancel,
  onDelete,
  shareControl,
}: {
  entry: ExpertiseEntry;
  editable: boolean;
  fields: readonly FieldDef[];
  isDraft?: boolean;
  error?: string;
  onPatch: (patch: Record<string, unknown>) => void;
  onCancel?: () => void;
  onDelete?: () => void;
  shareControl?: ReactNode;
}) {
  const intl = useIntl();
  const lvl = levelFromPercent(Number(entry.level));
  const levelLabel = lvl ? intl.formatMessage({ id: lvl.nameId, defaultMessage: lvl.name }) : "–";
  const missing = computeMissing(fields, entry as unknown as Record<string, unknown>);
  return (
    <EditableRow
      rowId={`profile-expertise-${entry.id}`}
      id={`profile-expertise-${entry.id}`}
      editable={editable}
      className="py-2.5 first:pt-0 last:pb-0"
      onCancel={onCancel}
      onDelete={onDelete}
      missingFields={missing}
      topRight={shareControl ? <ForcedShare>{shareControl}</ForcedShare> : undefined}
      confirmLabel={
        isDraft
          ? intl.formatMessage({ id: "common.add", defaultMessage: "Add" })
          : intl.formatMessage({ id: "profile.rows.done", defaultMessage: "Done" })
      }
      renderDisplay={() => (
        <>
          <div className="flex items-baseline gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-smycles-body font-medium leading-tight transition group-hover:text-ink-accent">
                {entry.description || "–"}
              </div>
              <div className="smycles-field-meta flex items-center gap-1 text-ink-muted">
                <span>
                  <FormattedMessage
                    id="profile.rows.level"
                    defaultMessage="Level: {level}"
                    values={{ level: levelLabel }}
                  />
                </span>
                {lvl ? (
                  <SkillLevelInfoIcon
                    level={lvl}
                    className="opacity-0 transition group-hover:opacity-100 group-focus-within:opacity-100"
                  />
                ) : null}
              </div>
            </div>
            {shareControl}
          </div>
          <EntryRowError message={error} />
        </>
      )}
      renderEdit={() => (
        <EntryFields
          fields={fields}
          values={entry as unknown as Record<string, string>}
          onChange={(k, v) => onPatch({ [k]: v })}
        />
      )}
    />
  );
}

export function CertificateRow({
  entry,
  editable,
  fields,
  isDraft = false,
  error,
  onPatch,
  onCancel,
  onDelete,
  shareControl,
}: {
  entry: CertificateEntry;
  editable: boolean;
  fields: readonly FieldDef[];
  isDraft?: boolean;
  error?: string;
  onPatch: (patch: Record<string, unknown>) => void;
  onCancel?: () => void;
  onDelete?: () => void;
  shareControl?: ReactNode;
}) {
  const intl = useIntl();
  const missing = computeMissing(fields, entry as unknown as Record<string, unknown>);
  return (
    <EditableRow
      rowId={`profile-certificate-${entry.id}`}
      id={`profile-certificate-${entry.id}`}
      editable={editable}
      className="py-2.5 first:pt-0 last:pb-0"
      onCancel={onCancel}
      onDelete={onDelete}
      missingFields={missing}
      topRight={shareControl ? <ForcedShare>{shareControl}</ForcedShare> : undefined}
      confirmLabel={
        isDraft
          ? intl.formatMessage({ id: "common.add", defaultMessage: "Add" })
          : intl.formatMessage({ id: "profile.rows.done", defaultMessage: "Done" })
      }
      renderDisplay={() => (
        <>
          <div className="flex items-baseline justify-between gap-4">
            <div className="flex min-w-0 flex-1 items-baseline gap-3">
              <div
                id={`profile-certificatename-${entry.id}`}
                className="text-smycles-body font-medium leading-tight transition group-hover:text-ink-accent"
              >
                {entry.name || "–"}
              </div>
            </div>
            <div className="flex shrink-0 items-baseline gap-3">
              {entry.date ? (
                <span className="text-smycles-label tabular-nums text-ink-muted">
                  {fmtMonthYear(entry.date)}
                </span>
              ) : null}
              {shareControl}
            </div>
          </div>
          <EntryRowError message={error} />
        </>
      )}
      renderEdit={() => (
        <EntryFields
          fields={fields}
          values={entry as unknown as Record<string, string>}
          onChange={(k, v) => onPatch({ [k]: v })}
        />
      )}
    />
  );
}
